import React, { JSX, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'

import {
  ExpandedItem,
  ExpandedRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableRow,
} from '@ui/v2/Table/Table'

import { PageQuery } from '@utils/ApiClient'
import { formatDate } from '@utils/date.utils'

import { FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'
import {
  SubscriptionHistoryItem,
  getSubscriptionRequestTypeLabel,
  showSubLevel,
} from '@model/SubscriptionsDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { StatusBadge, SubscriptionBadgeMap } from '@shared/components/StatusBadge/StatusBadge'

import { SubscriptionStatusHistoryModal } from './modals/SubscriptionStatusHistory.modal'

interface SubscriptionsHistoryTableProps {
  data: SubscriptionHistoryItem[]
  search: SearchResponseProps
  sortOptions?: NameDto<string>[]
  noResults: boolean | JSX.Element
  isLoading?: boolean
}

interface SearchResponseProps {
  search: FilterQueryProps
  searchTerm: string | null
  pageQuery: PageQuery | undefined
  setPageQuery(value: PageQuery): void
}

export const SubscriptionsHistoryTable: React.FC<SubscriptionsHistoryTableProps> = (props) => {
  const { data, search, isLoading = false, noResults, sortOptions = [] } = props
  const { pageQuery, setPageQuery } = search

  const { formatMoney } = useFormatNumber()
  const { t } = useTranslation()
  const { scrollIntoView } = useScrollToElementIds()

  const [expandedRow, setExpandedRow] = useState('')
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name

  const historyStatusModal = useModal(SubscriptionStatusHistoryModal)

  const setSort = (sort?: string | null, sortOrder?: 'ASC' | 'DESC') => {
    if (!setPageQuery) return
    setPageQuery({ ...pageQuery, sort: sort ?? undefined, sortOrder })
  }

  const handleSetExpandedRow = (id: string, rowIndex: number) => {
    const next = id === expandedRow ? '' : id
    if (next) scrollIntoView([`subscription-history-etd-item-${rowIndex + 1}`])
    setExpandedRow(next)
  }

  return (
    <React.Fragment>
      <Table isLoading={isLoading} noResults={noResults}>
        <TableHeader>
          <TableRow>
            {sortOptions.map((x, idx) =>
              x.id ? (
                <TableHeadSort
                  id={x.id}
                  key={x.id}
                  align={idx === 0 ? 'left' : 'center'}
                  sort={pageQuery?.sort}
                  sortOrder={pageQuery?.sortOrder}
                  setSort={(sort, sortOrder) => setSort(sort, sortOrder)}
                >
                  {x.name}
                </TableHeadSort>
              ) : (
                <TableHead align={idx === 0 ? 'left' : 'center'} key={x.name}>
                  {x.name}
                </TableHead>
              )
            )}
          </TableRow>
        </TableHeader>

        <TableBody>
          {data.map((x, index) => {
            const isRowSelected = expandedRow === x.id
            return (
              <React.Fragment key={x.id}>
                <TableRow id={`subscription-history-etd-item-${index + 1}`}>
                  <TableCell
                    align='left'
                    width={200}
                    onExpand={() => handleSetExpandedRow(x.id, index)}
                    expanded={isRowSelected}
                  >
                    {formatDate(x.createdDate, { formatType: dateFormat })}
                  </TableCell>

                  <TableCell align='center'>
                    <strong>{x.subscription.name}</strong>
                  </TableCell>

                  <TableCell align='center' width={220}>
                    {getSubscriptionRequestTypeLabel(x.type.id, t)}
                  </TableCell>

                  <TableCell align='center' width={160}>
                    {formatMoney(x.amount)}
                  </TableCell>

                  <TableCell align='center'>
                    <StatusBadge
                      onClick={() => historyStatusModal.open({ id: x.id })}
                      badgeMap={SubscriptionBadgeMap}
                      {...x.state}
                    />
                  </TableCell>
                </TableRow>

                <ExpandedRow expanded={isRowSelected} colSpan={5}>
                  <ExpandedItem
                    label={t('Subscriptions.Product Group')}
                    value={x.subscriptionGroupType.name}
                  />
                  <ExpandedItem
                    label={t('Subscriptions.Level')}
                    value={x.subscriptionDetailType.id}
                    condition={showSubLevel(
                      x.subscriptionGroupType.id,
                      x.subscriptionDetailType.id
                    )}
                  />
                </ExpandedRow>
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </React.Fragment>
  )
}
